import { format, parseISO } from "date-fns";
import { DocumentTaxonomy } from "@common/documentCategories";
import { fullMonthFullYearFormat } from "@common/helpers/date";
import { FinancialAccountType } from "@common/types/financialAccount";
import { TaskType } from "@common/types/task";

export const ACCOUNT_TYPE_TO_TAXONOMY: Partial<
    Record<FinancialAccountType, DocumentTaxonomy>
> = {
    [FinancialAccountType.CHECKING]: DocumentTaxonomy.bank_statement,
    [FinancialAccountType.SAVINGS]: DocumentTaxonomy.bank_statement,
    [FinancialAccountType.CREDIT_CARD]: DocumentTaxonomy.credit_card_statement,
    [FinancialAccountType.PAYMENT_PROCESSOR]:
        DocumentTaxonomy.payment_processor_statement,
};

const STATEMENT_TITLE_PREFIX = "Upload monthly statements";

export const isGeneratedStatementTitle = (title: string): boolean =>
    title.trim() === "" || title.startsWith(STATEMENT_TITLE_PREFIX);

interface StatementRetrievalTitleParams {
    periodStart: string;
    periodEnd: string;
    accountName: string | null;
}

export const buildStatementRetrievalTitle = ({
    periodStart,
    periodEnd,
    accountName,
}: StatementRetrievalTitleParams): string => {
    const startDate = parseISO(periodStart);
    const endDate = parseISO(periodEnd);
    const isFullYear = startDate.getMonth() === 0 && endDate.getMonth() === 11;
    const isSameYear = startDate.getFullYear() === endDate.getFullYear();
    const isSameMonth =
        isSameYear && startDate.getMonth() === endDate.getMonth();

    const accountLabel = accountName ? `for "${accountName}" ` : "";

    let periodLabel: string;
    if (isFullYear && isSameYear) {
        periodLabel = `for ${startDate.getFullYear()}`;
    } else if (isSameMonth) {
        periodLabel = `for ${format(endDate, fullMonthFullYearFormat)}`;
    } else {
        const startFormat = isSameYear ? "MMM" : fullMonthFullYearFormat;
        periodLabel = `for ${format(startDate, startFormat)} to ${format(endDate, fullMonthFullYearFormat)}`;
    }

    return `${STATEMENT_TITLE_PREFIX} ${accountLabel}${periodLabel}`;
};

interface StatementTitleAccount {
    id: number;
    name?: string | null;
}

interface StatementFilters {
    periodStart?: string;
    periodEnd?: string;
    accountIds?: number[];
}

export const getStatementRetrievalTitle = ({
    filters,
    accounts,
}: {
    filters: StatementFilters | undefined;
    accounts: StatementTitleAccount[];
}): string | null => {
    const accountId = filters?.accountIds?.[0];
    const account = accounts.find(({ id }) => id === accountId);
    if (!account || !filters?.periodStart || !filters.periodEnd) {
        return null;
    }
    return buildStatementRetrievalTitle({
        periodStart: filters.periodStart,
        periodEnd: filters.periodEnd,
        accountName: account.name ?? null,
    });
};

export const getTitleAfterWorkflowChange = ({
    title,
    nextTaskType,
}: {
    title: string;
    nextTaskType: TaskType;
}): string =>
    nextTaskType !== TaskType.DOCUMENT_RETRIEVAL &&
    title.startsWith(STATEMENT_TITLE_PREFIX)
        ? ""
        : title;
